import { api, unwrap } from "@lumberset/api-client";
import { defaultStandards, type EstimatingStandards } from "@lumberset/estimating";
import { date } from "@lumberset/takeoff-ui";
import {
  Button,
  Container,
  Field,
  Input,
  SectionHeader,
  Select,
  Switch,
  toast,
} from "@lumberset/ui";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { standardsQuery } from "../../../lib/queries";

export const Route = createFileRoute("/_app/settings/standards")({
  loader: ({ context: { queryClient } }) => queryClient.ensureQueryData(standardsQuery),
  component: Standards,
});

/** Only what differs from LumberSet's defaults is stored, so a Yard keeps inheriting improved defaults. */
function overridesOf(current: unknown, defaults: unknown): unknown {
  if (Array.isArray(current) || typeof current !== "object" || current === null) {
    return JSON.stringify(current) === JSON.stringify(defaults) ? undefined : current;
  }
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(current)) {
    const changed = overridesOf(value, (defaults as Record<string, unknown> | undefined)?.[key]);
    if (changed !== undefined) out[key] = changed;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

const wasteLabels: [keyof EstimatingStandards["waste"], string][] = [
  ["studs", "Studs"],
  ["plates", "Plates"],
  ["headers", "Headers"],
  ["blocking", "Blocking"],
  ["joists", "Joists"],
  ["rim", "Rim board"],
  ["rafters", "Rafters"],
  ["ridgeHipValley", "Ridge, hips, valleys"],
  ["fascia", "Fascia"],
  ["wallSheathing", "Wall sheathing"],
  ["roofSheathing", "Roof sheathing"],
  ["subfloor", "Subfloor"],
];

/** Sizes a Yard blocks between trusses with, of the nominal sizes the engine knows. */
const blockingSizes: EstimatingStandards["roofs"]["trussBlockingNominal"][] = [
  "2x4",
  "2x6",
  "2x8",
  "2x10",
  "2x12",
];

type Companion = keyof EstimatingStandards["materials"]["anchorBoltCompanions"];

/**
 * What the takeoff assumes where the drawings are silent, each a switch: the
 * engine's judgement calls 18 to 24, flagged on the takeoff for the Yard to
 * confirm. Each row reads and writes its own place in the standards.
 */
const assumptions: {
  key: string;
  label: string;
  hint: string;
  get: (standards: EstimatingStandards) => boolean;
  set: (standards: EstimatingStandards, on: boolean) => EstimatingStandards;
}[] = [
  {
    key: "assumeExteriorSheathing",
    label: "Sheathing on exterior walls",
    hint: "Exterior walls with no sheathing on the drawings are sheathed on one face, and flagged for you to confirm.",
    get: (standards) => standards.walls.assumeExteriorSheathing,
    set: (standards, on) => ({
      ...standards,
      walls: { ...standards.walls, assumeExteriorSheathing: on },
    }),
  },
  {
    key: "anchorBoltsOnPartitions",
    label: "Anchor bolts on interior partitions",
    hint: "Partitions and plumbing walls on a slab get anchor bolts like every other wall on concrete. Off, they are left to be pinned.",
    get: (standards) => standards.walls.anchorBoltsOnPartitions,
    set: (standards, on) => ({
      ...standards,
      walls: { ...standards.walls, anchorBoltsOnPartitions: on },
    }),
  },
  {
    key: "assumeRim",
    label: "Rim board",
    hint: "A floor or deck with no rim length on the drawings gets rim board around its outline, and is flagged.",
    get: (standards) => standards.floors.assumeRim,
    set: (standards, on) => ({ ...standards, floors: { ...standards.floors, assumeRim: on } }),
  },
  {
    key: "assumeFascia",
    label: "Fascia",
    hint: "A roof with no fascia length on the drawings gets fascia along every eave, rakes not included, and is flagged.",
    get: (standards) => standards.roofs.assumeFascia,
    set: (standards, on) => ({ ...standards, roofs: { ...standards.roofs, assumeFascia: on } }),
  },
  {
    key: "eaveBlocking",
    label: "Eave blocking",
    hint: "Solid blocking between rafters or trusses along every eave, one block a space, flagged as assumed.",
    get: (standards) => standards.roofs.eaveBlocking,
    set: (standards, on) => ({ ...standards, roofs: { ...standards.roofs, eaveBlocking: on } }),
  },
  ...(
    [
      [
        "nut",
        "Nuts on anchor bolts",
        "One hex nut for each anchor bolt, listed under anchor bolts and flagged as assumed.",
      ],
      [
        "cutWasher",
        "Cut washers on anchor bolts",
        "One cut washer for each anchor bolt, listed under anchor bolts and flagged as assumed.",
      ],
      [
        "plateWasher",
        "Plate washers on anchor bolts",
        "One 3 in. square plate washer for each anchor bolt, for sites where the seismic design category calls for them.",
      ],
    ] as [Companion, string, string][]
  ).map(([companion, label, hint]) => ({
    key: companion,
    label,
    hint,
    get: (standards: EstimatingStandards) => standards.materials.anchorBoltCompanions[companion],
    set: (standards: EstimatingStandards, on: boolean) => ({
      ...standards,
      materials: {
        ...standards.materials,
        anchorBoltCompanions: { ...standards.materials.anchorBoltCompanions, [companion]: on },
      },
    }),
  })),
];

const scopeLabels: [keyof EstimatingStandards["scope"], string, string][] = [
  ["walls", "Wall framing", "Studs, plates, headers, blocking"],
  ["floors", "Floor and ceiling framing", "Joists, rim, blocking"],
  ["roofs", "Roof framing", "Rafters, ridge, hips, valleys, fascia"],
  ["trusses", "Trusses", "Listed as a counted line for your truss supplier"],
  ["beamsAndPosts", "Beams and posts", "Sawn and engineered"],
  ["sheathing", "Sheathing and subfloor", "Wall, roof and floor panels"],
  ["hardware", "Hardware", "Hangers, hold-downs, straps, ties"],
  ["anchorBolts", "Anchor bolts", "Along treated sill plates"],
];

function Standards() {
  const queryClient = useQueryClient();
  const { yard } = Route.useRouteContext();
  const canEdit = yard.role !== "member";
  const { data } = useSuspenseQuery(standardsQuery);
  const [draft, setDraft] = useState<EstimatingStandards>(data.effective);
  const [stockText, setStockText] = useState(data.effective.stock.sawnFt.join(", "));

  const set = <
    Group extends "walls" | "floors" | "roofs" | "waste" | "stock" | "materials" | "scope",
  >(
    group: Group,
    values: Partial<EstimatingStandards[Group]>,
  ) => setDraft((current) => ({ ...current, [group]: { ...current[group], ...values } }));

  const save = useMutation({
    mutationFn: () => {
      const { version: _version, ...current } = draft;
      const { version: _defaultVersion, ...defaults } = defaultStandards();
      return unwrap(
        api.yard.standards.$put({
          json: { overrides: (overridesOf(current, defaults) ?? {}) as Record<string, unknown> },
        }),
      );
    },
    onSuccess: async ({ version }) => {
      await queryClient.invalidateQueries({ queryKey: ["yard", "standards"] });
      toast.success(`Saved as version ${version}`, {
        description:
          "New takeoffs use it. Earlier takeoffs keep the standards they were made with.",
      });
    },
    onError: (error) => toast.error(error.message),
  });

  const number = (value: string, fallback: number) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && value.trim() !== "" ? parsed : fallback;
  };

  return (
    <fieldset disabled={!canEdit} className="flex flex-col gap-y-3">
      <Container className="divide-y p-0">
        <SectionHeader
          title="How your yard frames and orders"
          description={`These turn the building on the drawings into quantities. Version ${data.version}, saved ${date(
            data.history.find((entry) => entry.isCurrent)?.createdAt,
          )}. Values on the drawings always win over these defaults.`}
        />

        <div className="grid gap-5 px-6 py-5 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Stud spacing when drawings are silent">
            {(control) => (
              <Input
                {...control}
                inputMode="decimal"
                suffix="in. o.c."
                defaultValue={draft.walls.studSpacingIn}
                onChange={(event) =>
                  set("walls", { studSpacingIn: number(event.target.value, 16) })
                }
              />
            )}
          </Field>
          <Field label="Studs per corner">
            {(control) => (
              <Input
                {...control}
                inputMode="numeric"
                defaultValue={draft.walls.studsPerCorner}
                onChange={(event) =>
                  set("walls", { studsPerCorner: Math.round(number(event.target.value, 3)) })
                }
              />
            )}
          </Field>
          <Field label="Extra studs per wall intersection">
            {(control) => (
              <Input
                {...control}
                inputMode="numeric"
                defaultValue={draft.walls.studsPerIntersection}
                onChange={(event) =>
                  set("walls", { studsPerIntersection: Math.round(number(event.target.value, 2)) })
                }
              />
            )}
          </Field>
          <Field
            label="Stud lengths"
            hint="Precut studs for 8, 9 and 10 ft walls, where you stock them."
          >
            {(control) => (
              <Select
                value={draft.walls.studLength}
                onValueChange={(value) => set("walls", { studLength: value as "precut" | "stock" })}
              >
                <Select.Trigger id={control.id}>
                  <Select.Value />
                </Select.Trigger>
                <Select.Content>
                  <Select.Item value="precut">Order precut studs</Select.Item>
                  <Select.Item value="stock">Order full stock lengths</Select.Item>
                </Select.Content>
              </Select>
            )}
          </Field>
          <Field label="Top plates">
            {(control) => (
              <Input
                {...control}
                inputMode="numeric"
                defaultValue={draft.walls.topPlates}
                onChange={(event) =>
                  set("walls", { topPlates: Math.round(number(event.target.value, 2)) })
                }
              />
            )}
          </Field>
          <Field label="Rough sill plates per window" hint="2 where you double the sills.">
            {(control) => (
              <Input
                {...control}
                inputMode="numeric"
                defaultValue={draft.walls.windowSillPlates}
                onChange={(event) =>
                  set("walls", { windowSillPlates: Math.round(number(event.target.value, 1)) })
                }
              />
            )}
          </Field>
          <Field label="Plate length you prefer to ship">
            {(control) => (
              <Input
                {...control}
                inputMode="decimal"
                suffix="ft"
                defaultValue={draft.stock.plateFt}
                onChange={(event) => set("stock", { plateFt: number(event.target.value, 16) })}
              />
            )}
          </Field>
          <Field label="Joist spacing when drawings are silent">
            {(control) => (
              <Input
                {...control}
                inputMode="decimal"
                suffix="in. o.c."
                defaultValue={draft.floors.joistSpacingIn}
                onChange={(event) =>
                  set("floors", { joistSpacingIn: number(event.target.value, 16) })
                }
              />
            )}
          </Field>
          <Field label="Rafter and truss spacing when silent">
            {(control) => (
              <Input
                {...control}
                inputMode="decimal"
                suffix="in. o.c."
                defaultValue={draft.roofs.rafterSpacingIn}
                onChange={(event) => {
                  const spacing = number(event.target.value, 24);
                  set("roofs", { rafterSpacingIn: spacing, trussSpacingIn: spacing });
                }}
              />
            )}
          </Field>
          <Field
            label="Blocking between trusses"
            hint="Along the eaves, when eave blocking is on. Rafters get their own size."
          >
            {(control) => (
              <Select
                value={draft.roofs.trussBlockingNominal}
                onValueChange={(value) =>
                  set("roofs", { trussBlockingNominal: value as (typeof blockingSizes)[number] })
                }
              >
                <Select.Trigger id={control.id}>
                  <Select.Value />
                </Select.Trigger>
                <Select.Content>
                  {blockingSizes.map((size) => (
                    <Select.Item key={size} value={size}>
                      {size}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select>
            )}
          </Field>
          <Field label="Anchor bolt diameter" hint="Names the nut and washers for each bolt.">
            {(control) => (
              <Input
                {...control}
                inputMode="decimal"
                suffix="in."
                defaultValue={draft.materials.anchorBoltDiameterIn}
                onChange={(event) =>
                  set("materials", { anchorBoltDiameterIn: number(event.target.value, 0.5) })
                }
              />
            )}
          </Field>
          <Field
            label="Stock lengths you carry"
            hint="In feet, separated by commas."
            className="sm:col-span-2 lg:col-span-1"
          >
            {(control) => (
              <Input
                {...control}
                value={stockText}
                onChange={(event) => {
                  setStockText(event.target.value);
                  const lengths = event.target.value
                    .split(",")
                    .map((part) => Number(part.trim()))
                    .filter((length) => Number.isFinite(length) && length > 0)
                    .sort((a, b) => a - b);
                  if (lengths.length > 0) set("stock", { sawnFt: lengths });
                }}
              />
            )}
          </Field>
        </div>
      </Container>

      <Container className="divide-y p-0">
        <SectionHeader
          title="Waste"
          description="Added to what the drawings require, before rounding up to whole pieces."
        />
        <div className="grid grid-cols-2 gap-5 px-6 py-5 sm:grid-cols-3 lg:grid-cols-4">
          {wasteLabels.map(([key, label]) => (
            <Field key={key} label={label}>
              {(control) => (
                <Input
                  {...control}
                  inputMode="decimal"
                  suffix="%"
                  defaultValue={Math.round(draft.waste[key] * 1000) / 10}
                  onChange={(event) =>
                    set("waste", {
                      [key]: Math.min(50, Math.max(0, number(event.target.value, 0))) / 100,
                    })
                  }
                />
              )}
            </Field>
          ))}
        </div>
      </Container>

      <Container className="divide-y p-0">
        <SectionHeader
          title="Assumed where the drawings are silent"
          description="Each is listed with a flag on the takeoff, so you can confirm or correct it. Switch off what your yard frames or supplies differently."
        />
        <ul className="divide-y divide-ui-border-base">
          {assumptions.map((row) => (
            <li key={row.key} className="flex items-center justify-between gap-x-4 px-6 py-3.5">
              <label htmlFor={`assume-${row.key}`} className="min-w-0 cursor-pointer">
                <span className="txt-compact-small-plus block text-ui-fg-base">{row.label}</span>
                <span className="txt-compact-xsmall block text-ui-fg-muted">{row.hint}</span>
              </label>
              <Switch
                id={`assume-${row.key}`}
                checked={row.get(draft)}
                onCheckedChange={(checked) => setDraft((current) => row.set(current, checked))}
              />
            </li>
          ))}
        </ul>
      </Container>

      <Container className="divide-y p-0">
        <SectionHeader
          title="What you quote"
          description="Switch off anything your yard does not supply. It is left out, with a note on the takeoff."
        />
        <ul className="divide-y divide-ui-border-base">
          {scopeLabels.map(([key, label, hint]) => (
            <li key={key} className="flex items-center justify-between gap-x-4 px-6 py-3.5">
              <label htmlFor={`scope-${key}`} className="min-w-0 cursor-pointer">
                <span className="txt-compact-small-plus block text-ui-fg-base">{label}</span>
                <span className="txt-compact-xsmall block text-ui-fg-muted">{hint}</span>
              </label>
              <Switch
                id={`scope-${key}`}
                checked={draft.scope[key]}
                onCheckedChange={(checked) => set("scope", { [key]: checked })}
              />
            </li>
          ))}
        </ul>
      </Container>

      {canEdit ? (
        <div className="flex justify-end">
          <Button isLoading={save.isPending} onClick={() => save.mutate()}>
            Save as new version
          </Button>
        </div>
      ) : (
        <p className="txt-small px-1 text-ui-fg-muted">
          Only an Owner or Manager can change estimating standards.
        </p>
      )}
    </fieldset>
  );
}
